(() => {
  const elementName = 'acm-vehicle-finder';

  if (customElements.get(elementName)) return;

  class AcmVehicleFinder extends HTMLElement {
    constructor() {
      super();
      this.ymm = Object.create(null);
      this.queries = Object.create(null);
      this.initialized = false;
      this.frame = null;
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.eventController = new AbortController();
      this.fetchController = new AbortController();
      this.cacheElements();
      this.resetControls();
      this.bindEvents();
      this.setupHeroMotion();
      this.loadProducts();
    }

    disconnectedCallback() {
      this.eventController?.abort();
      this.fetchController?.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.initialized = false;
    }

    cacheElements() {
      this.form = this.querySelector('[data-acm-finder-form]');
      this.makeSelect = this.querySelector('[data-acm-make]');
      this.modelSelect = this.querySelector('[data-acm-model]');
      this.yearSelect = this.querySelector('[data-acm-year]');
      this.submitButton = this.querySelector('[data-acm-submit]');
      this.status = this.querySelector('[data-acm-status]');
    }

    bindEvents() {
      const signal = this.eventController.signal;

      this.makeSelect?.addEventListener(
        'change',
        () => this.fillModels(this.makeSelect.value),
        { signal },
      );

      this.modelSelect?.addEventListener(
        'change',
        () => this.fillYears(
          this.makeSelect.value,
          this.modelSelect.value,
        ),
        { signal },
      );

      this.yearSelect?.addEventListener(
        'change',
        () => this.updateSubmitState(),
        { signal },
      );

      this.form?.addEventListener(
        'submit',
        (event) => this.handleSubmit(event),
        { signal },
      );
    }

    resetControls() {
      this.ymm = Object.create(null);
      this.queries = Object.create(null);

      this.resetSelect(
        this.makeSelect,
        this.dataset.brandPlaceholder,
      );

      this.resetSelect(
        this.modelSelect,
        this.dataset.modelPlaceholder,
      );

      this.resetSelect(
        this.yearSelect,
        this.dataset.yearPlaceholder,
      );

      this.makeSelect.disabled = true;
      this.modelSelect.disabled = true;
      this.yearSelect.disabled = true;
      this.submitButton.disabled = true;

      this.setStatus('loading', this.dataset.loadingText);
    }

    resetSelect(select, placeholder) {
      if (!select) return;

      select.replaceChildren(
        this.createOption('', placeholder || ''),
      );
    }

    createOption(value, label) {
      const option = this.ownerDocument.createElement('option');
      option.value = value;
      option.textContent = label;

      return option;
    }

    async loadProducts() {
      let page = 1;
      let more = true;
      let loadedProducts = 0;
      let partialFailure = false;

      try {
        while (more && page < 40) {
          let response;
          let data;

          try {
            response = await fetch(
              `/collections/all/products.json?limit=250&page=${page}`,
              {
                cache: 'no-store',
                signal: this.fetchController.signal,
              },
            );

            if (!response.ok) {
              throw new Error(
                `Vehicle catalog request failed with ${response.status}`,
              );
            }

            data = await response.json();
          } catch (error) {
            if (error.name === 'AbortError') return;

            if (loadedProducts > 0) {
              partialFailure = true;
              break;
            }

            throw error;
          }

          const products = Array.isArray(data?.products)
            ? data.products
            : (data?.products?.products || []);

          if (!products || products.length === 0) {
            more = false;
            break;
          }

          loadedProducts += products.length;

          for (const product of products) {
            try {
              this.addProduct(product);
            } catch (error) {
              // Hatalı bir ürün diğer ürünlerin yüklenmesini engellemez.
            }
          }

          page += 1;
        }

        if (this.fetchController.signal.aborted) return;

        const makeCount = Object.keys(this.ymm).length;

        if (makeCount === 0) {
          this.setStatus('empty', this.dataset.emptyText);
          return;
        }

        this.fillMakes();
        this.makeSelect.disabled = false;

        if (partialFailure) {
          this.setStatus('partial', this.dataset.partialText);
        } else {
          this.setStatus('ready', '');
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setStatus('error', this.dataset.errorText);
      }
    }

    addProduct(product) {
      if (!product || typeof product.title !== 'string') return;

      const titleData = this.parseVehicleTitle(
        product.title.trim(),
      );

      const tags = this.getTags(product.tags);
      const makeTag = this.findPrefixedTag(tags, 'Make_');
      const modelTag = this.findPrefixedTag(tags, 'Model_');

      const yearTags = tags.filter((tag) =>
        /^Year_\d{4}$/i.test(tag),
      );

      const brand = makeTag
        ? this.cleanTagValue(makeTag, 'Make_')
        : titleData?.brand;

      const model = modelTag
        ? this.cleanTagValue(modelTag, 'Model_')
        : titleData?.model;

      const years = yearTags.length > 0
        ? yearTags.map((tag) => tag.replace(/^Year_/i, ''))
        : titleData?.years || [];

      if (!brand || !model || years.length === 0) return;

      if (!this.ymm[brand]) {
        this.ymm[brand] = Object.create(null);
      }

      if (!this.ymm[brand][model]) {
        this.ymm[brand][model] = new Set();
      }

      if (!this.queries[brand]) {
        this.queries[brand] = Object.create(null);
      }

      if (!this.queries[brand][model]) {
        this.queries[brand][model] = Object.create(null);
      }

      years.forEach((year) => {
        this.ymm[brand][model].add(year);

        const exactYearTag = yearTags.find(
          (tag) =>
            tag.toLowerCase()
            === `year_${year}`.toLowerCase(),
        );

        const searchTags =
          makeTag && modelTag && exactYearTag
            ? [makeTag, modelTag, exactYearTag]
            : [brand, model, year];

        this.queries[brand][model][year] =
          this.buildTagQuery(searchTags);
      });
    }

    getTags(value) {
      if (Array.isArray(value)) {
        return value
          .map(String)
          .map((tag) => tag.trim())
          .filter(Boolean);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);
      }

      return [];
    }

    findPrefixedTag(tags, prefix) {
      const normalizedPrefix = prefix.toLowerCase();

      return tags.find((tag) =>
        tag.toLowerCase().startsWith(normalizedPrefix),
      );
    }

    cleanTagValue(tag, prefix) {
      return tag
        .slice(prefix.length)
        .replace(/[_-]+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
    }

    parseVehicleTitle(title) {
      const match = title.match(
        /FOR\s+(.+?)\s+(\d{4})(?:[–-](\d{4}))?/i,
      );

      if (!match) return null;

      const vehicle = match[1]
        .replace(/\(.*?\)/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

      const multiWordMakes = [
        'Alfa Romeo',
        'Aston Martin',
        'DS Automobiles',
        'Great Wall',
        'Land Rover',
        'Mercedes Benz',
        'Rolls Royce',
      ];

      const lowerVehicle = vehicle.toLowerCase();

      const knownMake = multiWordMakes.find((make) =>
        lowerVehicle.startsWith(`${make.toLowerCase()} `),
      );

      const brand = knownMake || vehicle.split(/\s+/)[0];
      const model = vehicle.slice(brand.length).trim();

      if (!brand || !model) return null;

      return {
        brand,
        model,
        years: this.expandYears(match[2], match[3]),
      };
    }

    buildTagQuery(tags) {
      return tags
        .map(
          (tag) =>
            `tag:"${String(tag)
              .replace(/\\/g, '\\\\')
              .replace(/"/g, '\\"')}"`,
        )
        .join(' ');
    }

    expandYears(firstYear, lastYear) {
      const first = parseInt(firstYear, 10);
      const last = lastYear
        ? parseInt(lastYear, 10)
        : first;

      const from = Math.min(first, last);
      const to = Math.max(first, last);
      const years = [];

      for (let year = from; year <= to; year += 1) {
        years.push(String(year));
      }

      return years;
    }

    fillMakes() {
      this.resetSelect(
        this.makeSelect,
        this.dataset.brandPlaceholder,
      );

      const makes = Object.keys(this.ymm).sort(
        (first, second) =>
          first.localeCompare(second),
      );

      makes.forEach((make) => {
        this.makeSelect.appendChild(
          this.createOption(make, make),
        );
      });
    }

    fillModels(brand) {
      this.resetSelect(
        this.modelSelect,
        this.dataset.modelPlaceholder,
      );

      this.resetSelect(
        this.yearSelect,
        this.dataset.yearPlaceholder,
      );

      this.modelSelect.disabled = true;
      this.yearSelect.disabled = true;
      this.submitButton.disabled = true;

      if (!brand || !this.ymm[brand]) return;

      const models = Object.keys(this.ymm[brand]).sort(
        (first, second) =>
          first.localeCompare(second),
      );

      models.forEach((model) => {
        this.modelSelect.appendChild(
          this.createOption(model, model),
        );
      });

      this.modelSelect.disabled = models.length === 0;
    }

    fillYears(brand, model) {
      this.resetSelect(
        this.yearSelect,
        this.dataset.yearPlaceholder,
      );

      this.yearSelect.disabled = true;
      this.submitButton.disabled = true;

      const yearSet = this.ymm[brand]?.[model];

      if (!yearSet) return;

      const years = Array.from(yearSet)
        .map(Number)
        .sort((first, second) => second - first)
        .map(String);

      years.forEach((year) => {
        this.yearSelect.appendChild(
          this.createOption(year, year),
        );
      });

      this.yearSelect.disabled = years.length === 0;
    }

    updateSubmitState() {
      this.submitButton.disabled = !(
        this.makeSelect.value
        && this.modelSelect.value
        && this.yearSelect.value
      );
    }

    handleSubmit(event) {
      event.preventDefault();

      const make = this.makeSelect.value?.trim();
      const model = this.modelSelect.value?.trim();
      const year = this.yearSelect.value?.trim();

      if (!make || !model || !year) return;

      const query =
        this.queries[make]?.[model]?.[year]
        || this.buildTagQuery([make, model, year]);

      window.location.href =
        '/search?q=' + encodeURIComponent(query);
    }

    setStatus(kind, message) {
      this.dataset.state = kind;

      this.setAttribute(
        'aria-busy',
        kind === 'loading' ? 'true' : 'false',
      );

      if (!this.status) return;

      this.status.dataset.kind = kind;
      this.status.textContent = message || '';
      this.status.hidden = kind === 'ready';
    }

    setupHeroMotion() {
      if (
        window
          .matchMedia('(prefers-reduced-motion: reduce)')
          .matches
      ) {
        return;
      }

      const hero = this.closest(
        '[data-acm-vehicle-hero]',
      );

      const image = hero?.querySelector(
        '[data-acm-hero-image]',
      );

      if (!hero || !image) return;

      const update = () => {
        this.frame = null;

        const heroTop =
          hero.getBoundingClientRect().top;

        const offset = Math.min(
          Math.max(-heroTop * 0.06, 0),
          38,
        );

        image.style.setProperty(
          '--acm-parallax-y',
          `${offset}px`,
        );
      };

      const requestUpdate = () => {
        if (this.frame) return;

        this.frame =
          requestAnimationFrame(update);
      };

      update();

      window.addEventListener(
        'scroll',
        requestUpdate,
        {
          passive: true,
          signal: this.eventController.signal,
        },
      );

      window.addEventListener(
        'resize',
        requestUpdate,
        {
          passive: true,
          signal: this.eventController.signal,
        },
      );
    }
  }

  customElements.define(
    elementName,
    AcmVehicleFinder,
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      event.target
        .querySelectorAll(elementName)
        .forEach((finder) => {
          if (
            !finder.initialized
            && finder.isConnected
          ) {
            finder.connectedCallback();
          }
        });
    },
  );
})();
